import {
    Fylke,
    Kommune,
    Kontraktsomrade,
    Land,
    Omrade,
    Riksvegrute,
    Vegsystemreferanse,
} from '@/domain/omrader';
import { OmraderQueries } from '@/middleware/queries';
import { Polygon } from '@/domain/omrader/Polygon';

export class OmraderState {
    constructor(
        readonly land: Land[] = [],
        readonly fylke: Fylke[] = [],
        readonly kommune: Kommune[] = [],
        readonly kontraktsomrade: Kontraktsomrade[] = [],
        readonly riksvegrute: Riksvegrute[] = [],
        readonly vegsystemreferanse: Vegsystemreferanse[] = [],
        readonly polygon: Polygon | null = null,
        readonly loading: boolean = false
    ) {}

    override(modifyObject: {
        [P in keyof OmraderState]?: OmraderState[P];
    }): OmraderState {
        return Object.assign(Object.create(OmraderState.prototype), {
            ...this,
            ...modifyObject,
        });
    }

    withLand(land: Land[]): OmraderState {
        return this.override({ land });
    }

    withFylker(fylke: Fylke[]): OmraderState {
        return this.override({ fylke });
    }

    withKommuner(kommune: Kommune[]): OmraderState {
        return this.override({ kommune });
    }

    withRiksvegruter(riksvegrute: Riksvegrute[]): OmraderState {
        return this.override({ riksvegrute });
    }

    withKontraktsomrader(kontraktsomrade: Kontraktsomrade[]): OmraderState {
        return this.override({ kontraktsomrade });
    }

    withVegsystemreferanser(vegsystemreferanse: Vegsystemreferanse[]): OmraderState {
        return this.override({ vegsystemreferanse });
    }

    withLoading(loading: boolean) {
        return this.override({ loading });
    }

    withPolygon(polygon: Polygon | null): OmraderState {
        return this.override({ polygon });
    }

    getFullList(): Omrade[] {
        const areas: Omrade[] = [
            ...this.land,
            ...this.fylke,
            ...this.kommune,
            ...this.kontraktsomrade,
            ...this.riksvegrute,
            ...this.vegsystemreferanse,
        ];
        return this.polygon ? areas.concat(this.polygon) : areas;
    }

    getGeometryFetchables(): Omrade[] {
        return [
            ...this.fylke,
            ...this.kommune,
            ...this.kontraktsomrade,
            ...this.riksvegrute,
        ];
    }

    toQueries(): OmraderQueries {
        return new OmraderQueries(
            this.fylke.filter(omrade => omrade.visible).map(omrade => omrade.toParams()),
            this.kommune
                .filter(omrade => omrade.visible)
                .map(omrade => omrade.toParams()),
            this.kontraktsomrade
                .filter(omrade => omrade.visible)
                .map(omrade => omrade.toParams()),
            this.riksvegrute
                .filter(omrade => omrade.visible)
                .map(omrade => omrade.toParams()),
            this.vegsystemreferanse
                .filter(omrade => omrade.visible)
                .map(omrade => omrade.toParams()),
            this.polygon?.visible ? this.polygon.toParams() : null
        );
    }

    toOmradeQueries(kommuner: Kommune[] | undefined): OmraderQueries {
        const newState = this.toQueries();

        if (!(newState.kommune.length && newState.fylke.length)) {
            return newState;
        }

        // Les API tar snittet av kommuner og fylker, men i vegkart ønsker vi unionen. Derfor konverterer vi fylker til kommuner hvis begge parameterne er satt.
        let extraKommuner: string[] = [];
        if (kommuner) {
            extraKommuner = kommuner
                .filter(k =>
                    this.fylke
                        .map(f => f.nummer)
                        .includes(Math.floor(Number(k.nummer) / 100))
                )
                .map(k => k.nummer.toString());
        } else {
            extraKommuner = this.fylke
                .map(f => {
                    const fylkeNo = f.nummer.toString();
                    return Array.from(
                        { length: 100 },
                        (_, i) => fylkeNo + (i + 1).toString().padStart(2, '0')
                    );
                })
                .flat();
        }

        return newState
            .withFylker([])
            .withKommuner(Array.from(new Set([...newState.kommune, ...extraKommuner])));
    }
}
